import React from 'react';
import {
  Stack, Text, UnorderedList, withTranslation, StyledContainer, Trans,
} from '@piwikpro/ui-components';
import { TFunction } from 'react-i18next';

export type SubscriptionUpdateAction = 'upgrade' | 'downgrade'

interface ConfirmationModalBodyProps {
  action: SubscriptionUpdateAction
  immediateTransactionPrice: string
  recurringTransactionPrice: string
  nextBillingPeriod: string
  billingCycle: string
  creditsIssued: boolean
  rawImmediateTransactionPriceForComparison: number
  t: TFunction
}

const ConfirmationModalBody: React.FunctionComponent<ConfirmationModalBodyProps> = (
  {
    t,
    action,
    immediateTransactionPrice,
    recurringTransactionPrice,
    nextBillingPeriod,
    billingCycle,
    creditsIssued,
    rawImmediateTransactionPriceForComparison,
  },
) => {
  let body;
  const billingCycleTranslated = t(billingCycle === 'year' ? 'marketing.scheduled-action-information.period.yearly' : 'marketing.scheduled-action-information.period.monthly');

  const immediateTransactionMessage = () => {
    if (!creditsIssued) {
      if (rawImmediateTransactionPriceForComparison === 0) {
        return <Text as="p">{t('marketing.change-plan-overlay.upgrade-confirmation.description-2-with-no-charge')}</Text>;
      }
      return (
        <Text as="p">
          <Trans
            i18nKey="marketing.change-plan-overlay.upgrade-confirmation.description-2"
            values={{ value: immediateTransactionPrice }}
            components={[<b>text</b>]}
          />
        </Text>
      );
    }
    return (
      <Text as="p">
        {t('marketing.change-plan-overlay.upgrade-confirmation.description-2-with-credits', {
          value: immediateTransactionPrice,
          date: nextBillingPeriod,
        })}
      </Text>
    );
  };

  switch (action) {
    case 'upgrade':
      body = (
        <Stack vertical>
          <Stack.Item>
            <Text as="p">{t('marketing.change-plan-overlay.upgrade-confirmation.description-0')}</Text>
          </Stack.Item>
          <Stack.Item previousSpacing="none">
            <UnorderedList>
              <UnorderedList.Item>
                <Text as="p">{t('marketing.change-plan-overlay.upgrade-confirmation.description-1')}</Text>
              </UnorderedList.Item>
              <UnorderedList.Item>
                {immediateTransactionMessage()}
              </UnorderedList.Item>
              <UnorderedList.Item>
                {!creditsIssued ? (
                  <Trans
                    i18nKey="marketing.change-plan-overlay.upgrade-confirmation.description-3"
                    values={{
                      date: nextBillingPeriod,
                      value: recurringTransactionPrice,
                      billingCycle: billingCycleTranslated,
                    }}
                    components={[<b>text</b>]}
                  />
                ) : (
                  <Trans
                    i18nKey="marketing.change-plan-overlay.upgrade-confirmation.description-3-with-credits"
                    values={{
                      value: recurringTransactionPrice,
                      billingCycle: billingCycleTranslated,
                    }}
                    components={[<b>text</b>]}
                  />
                )}
              </UnorderedList.Item>
            </UnorderedList>
          </Stack.Item>
          <Stack.Item previousSpacing="narrow">
            <Text as="p">{t('marketing.change-plan-overlay.upgrade-confirmation.description-4')}</Text>
          </Stack.Item>
        </Stack>
      );
      break;
    case 'downgrade':
    default:
      body = (
        <Stack vertical>
          <Stack.Item>
            <Text as="p">
              <Trans
                i18nKey="marketing.change-plan-overlay.downgrade-confirmation.description-0"
                values={{
                  date: nextBillingPeriod,
                  value: recurringTransactionPrice,
                  billingCycle: billingCycleTranslated,
                }}
                components={[<b>text</b>]}
              />
            </Text>
          </Stack.Item>
          <Stack.Item previousSpacing="narrow">
            <Text as="p">{t('marketing.change-plan-overlay.downgrade-confirmation.description-1')}</Text>
          </Stack.Item>
        </Stack>
      );
      break;
  }

  return (
    <StyledContainer padding="extraNarrow">
      <Stack vertical spacing="narrow">
        {body}
      </Stack>
    </StyledContainer>
  );
};

export default withTranslation(['administration'])(ConfirmationModalBody);
